import { memo } from "react";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { Tooltip } from "ui/components/Tooltip";
import { Badge } from "ui/components/Badge";
import { MarketplaceEquipment } from "../../../types";
import { ChipGroupTitle, ChipToggle, SectionCard } from "../primitives";
import {
  AC_OPTIONS,
  AIRBAGS_OPTIONS,
  EQUIPMENT_ADDITIONAL,
  EQUIPMENT_ANTI_THEFT,
  EQUIPMENT_COMFORT,
  EQUIPMENT_SAFETY,
  RADIO_OPTIONS,
} from "services/api/sales/listings/consts";

interface Props {
  values: MarketplaceEquipment;
  onChange: (patch: Partial<MarketplaceEquipment>) => void;
  /** Oprema s vozila koju nijedan čip ne pokriva; prazno kad je sve prepoznato. */
  unmatched?: string[];
}

const EquipmentSection: React.FC<Props> = ({ values, onChange, unmatched = [] }) => {
  const toggle = (code: string) => {
    onChange({ selected: { ...values.selected, [code]: !values.selected[code] } });
  };

  const handleDropdown = (_e: any, name: string, item: { value: string }) =>
    onChange({ [name]: item.value } as Partial<MarketplaceEquipment>);

  // Oprema s vozila koju nijedan čip ne pokriva — badge javlja koliko ih je, popis je u tooltipu
  // da se vidi što treba ručno označiti ili dopisati u opis.
  const unmatchedBadge = unmatched.length > 0 && (
    <Tooltip
      position="bottom-left"
      interactive
      trigger={
        <span className="cursor-help whitespace-nowrap">
          <Badge text={`${unmatched.length} nepovezano`} variant="warning" height="small" showDot />
        </span>
      }
    >
      <div className="w-[440px] max-w-[80vw] max-h-80 overflow-auto text-left text-sm">
        <div className="font-semibold mb-2">S vozila, nije prepoznato:</div>
        <ul className="list-disc pl-5 space-y-1 text-slate-200">
          {unmatched.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </div>
    </Tooltip>
  );

  return (
    <SectionCard title="Oprema" action={unmatchedBadge}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <ChipGroupTitle>Dodatna oprema</ChipGroupTitle>
          <div className="flex flex-col gap-2">
            {EQUIPMENT_ADDITIONAL.map((c) => (
              <ChipToggle key={c.code} label={c.label} selected={!!values.selected[c.code]} onToggle={() => toggle(c.code)} block />
            ))}
          </div>
        </div>

        <div>
          <ChipGroupTitle>Sigurnost</ChipGroupTitle>
          <div className="flex flex-col gap-2">
            {EQUIPMENT_SAFETY.map((c) => (
              <ChipToggle key={c.code} label={c.label} selected={!!values.selected[c.code]} onToggle={() => toggle(c.code)} block />
            ))}
          </div>

          <div className="mt-4">
            <ChipGroupTitle>Sigurnost protiv krađe</ChipGroupTitle>
            <div className="flex flex-col gap-2">
              {EQUIPMENT_ANTI_THEFT.map((c) => (
                <ChipToggle key={c.code} label={c.label} selected={!!values.selected[c.code]} onToggle={() => toggle(c.code)} block />
              ))}
            </div>
          </div>

          <div className="mt-4">
            <Dropdown name="airbagsCode" label="Zračni jastuci" value={values.airbagsCode} options={AIRBAGS_OPTIONS} onChange={handleDropdown} />
          </div>
        </div>

        <div>
          <ChipGroupTitle>Udobnost</ChipGroupTitle>
          <div className="flex flex-col gap-2">
            {EQUIPMENT_COMFORT.map((c) => (
              <ChipToggle key={c.code} label={c.label} selected={!!values.selected[c.code]} onToggle={() => toggle(c.code)} block />
            ))}
          </div>

          <div className="mt-4 space-y-3">
            <div><Dropdown name="airConditioningCode" label="Klimatizacija" value={values.airConditioningCode} options={AC_OPTIONS} onChange={handleDropdown} /></div>
            <div><Dropdown name="carRadioCode" label="Autoradio" value={values.carRadioCode} options={RADIO_OPTIONS} onChange={handleDropdown} /></div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default memo(EquipmentSection);
